import { Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
}

// Default redirect target for unauthenticated access. Routes through to
// the login form (NOT the marketing landing page at `/`) so an admin
// whose session expired mid-task lands on something they can sign in
// from, rather than reading "Download our app".
export function ProtectedRoute({ children, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />
  }

  // Admin portal is locked to admin / super_admin only. Any other role —
  // tournament_organiser, player, feeder, umpire, viewer, or any custom
  // admin-created role — must use the mobile app. The primary block is at
  // the login screen (see AuthContext.login) which rejects non-admin
  // credentials before any token is issued; this guard is the second line
  // of defence (e.g. for a stale session whose role was downgraded
  // server-side after the token was issued).
  const normalizedRole = user?.role?.toLowerCase() ?? ''
  const hasAdminAccess =
    normalizedRole === 'admin' || normalizedRole === 'super_admin'

  if (!hasAdminAccess) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl font-semibold">Access Denied</h1>
        <p className="text-muted-foreground">
          The admin portal is restricted to platform administrators. Please use
          the mobile app to access your account.
        </p>
      </div>
    )
  }

  return <>{children}</>
}
